import { memo, useRef } from "react";

import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { twMerge } from "@/utils/twMerge";
import { useContextMenuKey } from "@/utils/useContextMenuKey";

import { useTreeItemControlTabIndex, useTreeItemFocusVisible } from "../useSidebarNav";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { RefObject } from "react";

export const ContextMenu = memo(function ContextMenu({
  items,
  title,
  rowRef,
  onOpenChange,
}: {
  items: DropdownItem[];
  /** Title of the row the menu acts on, read out in the button's name */
  title: string;
  /** The row whose Shift+F10 / Menu key opens this menu */
  rowRef: RefObject<HTMLElement | null>;
  onOpenChange?: (open: boolean) => void;
}) {
  const t = useTranslations();
  const buttonRef = useRef<HTMLButtonElement>(null);
  useContextMenuKey(rowRef, buttonRef);
  const controlTabIndex = useTreeItemControlTabIndex();
  const focusVisible = useTreeItemFocusVisible();

  return (
    <Dropdown
      anchor="bottom start"
      onOpenChange={onOpenChange}
      toggleButtonLabel={t("documentTree.contextMenu", { title })}
      toggleButtonProps={{ tabIndex: controlTabIndex, ref: buttonRef }}
      className={twMerge(
        "absolute z-10 top-1/2 -translate-y-1/2 right-0 mr-1",
        "opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 data-[open]:opacity-100",
        "sm:opacity-100",
        focusVisible && "opacity-100",
      )}
      toggleButtonComponent={
        <Sidebar.Button
          as="div"
          className={twMerge(
            "!rounded-4 !min-h-0 !min-w-0 w-6 h-6 my-auto",
            "hover:bg-blue-100 hover:dark:bg-blue-900 ",
            "sm:w-10 sm:h-10 sm:hover:bg-transparent sm:dark:hover:bg-transparent",
          )}
          icon={<Icon name="More" className="w-[20px] h-[20px] sm:w-[28px] sm:h-[28px]" />}
          inverted
        />
      }
      items={items}
      itemsClassName="fromSm:-translate-x-1/2 fromSm:ml-3 sm:-translate-x-7"
    />
  );
});
